import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import OrderStatusBadge from '@/components/orders/order-status-badge';
import { Box, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import { CommissionStatus } from '@fluenceur/types';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Define available status options (excluding 'All')
const commissionStatusOptions: { translationKey: string; value: CommissionStatus }[] = [
  { translationKey: 'filters.commissionStatus.pending', value: CommissionStatus.PENDING },
  { translationKey: 'filters.commissionStatus.processing', value: CommissionStatus.PROCESSING },
  { translationKey: 'filters.commissionStatus.paid', value: CommissionStatus.PAID },
  { translationKey: 'filters.commissionStatus.cancelled', value: CommissionStatus.CANCELLED },
];

interface StatusFilterProps {
  paramPrefix?: string; // Optional prefix for URL parameters
}

export const StatusFilter: React.FC<StatusFilterProps> = ({ paramPrefix = '' }) => {
  const { t } = useTranslation();

  // URL state management with nuqs
  const statusParam = paramPrefix ? `${paramPrefix}Status` : 'status';
  const [urlStatus, setUrlStatus] = useQueryState(statusParam, parseAsString);

  // Parse status from URL string
  const currentStatus = urlStatus as CommissionStatus | null;

  // Internal temporary state for the popover menu
  const [tempStatus, setTempStatus] = useState<CommissionStatus | null>(currentStatus);

  // Sync temp state if the URL value changes
  useEffect(() => {
    setTempStatus(currentStatus);
  }, [currentStatus]);

  const handleApply = useCallback(() => {
    setUrlStatus(tempStatus || null);
  }, [tempStatus, setUrlStatus]);

  const handleClear = useCallback(() => {
    setTempStatus(null);
    setUrlStatus(null);
  }, [setUrlStatus]);

  const isFilterActive = currentStatus !== null;

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('common.status')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>
            {currentStatus && <OrderStatusBadge status={currentStatus} size="xs" fontSize="10px" />}
          </FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="250px">
            <FilterButtonMenuHeader title={t('common.status')} onClearAll={handleClear} />
            <Box p={3}>
              <VStack gap={0.5} align="stretch">
                {commissionStatusOptions.map((option) => (
                  <FilterMenuItem
                    key={option.value}
                    onClick={() => setTempStatus(option.value)}
                    isSelected={tempStatus === option.value}
                  >
                    <Text fontSize="sm" fontWeight="normal">
                      {t(option.translationKey)}
                    </Text>
                  </FilterMenuItem>
                ))}
              </VStack>
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
